/**
 * Build queue — spec §9, §8 step 6.
 *
 * FIFO with head-of-line blocking: if the head cannot afford its materials, the
 * queue stalls rather than skipping ahead. Spec §9 is emphatic about why —
 * auto-skip silently reorders the player's plan and permanently starves
 * expensive buildings, because every refill cycle gets eaten by cheap items
 * queued ahead of them and the player never learns why their power plant never
 * arrived.
 *
 * Blocking is only tolerable if the stall explains itself, so the head always
 * reports what it is missing and roughly how long the wait is.
 */

import { BUILDINGS, GOOD_IDS, type BuildingId, type GoodId } from './data';
import {
  activePlots,
  buildTicksFor,
  isBuildable,
  isConnectedForPlanning,
  isReachable,
  plotAt,
} from './city';
import type { GameState, PlotCoord, StarvedInfo } from './types';

/** Window length before the production counters halve. See ProductionWindow. */
const PRODUCTION_WINDOW_TICKS = 128;

export type GoodTally = Partial<Record<GoodId, number>>;

export function canAfford(state: GameState, cost: GoodTally): boolean {
  for (const id of GOOD_IDS) {
    const required = cost[id];
    if (required === undefined) continue;
    if (state.inventory.goods[id] < required) return false;
  }
  return true;
}

function spend(state: GameState, cost: GoodTally): void {
  for (const id of GOOD_IDS) {
    const required = cost[id];
    if (required === undefined) continue;
    state.inventory.goods[id] -= required;
  }
}

/** Record this tick's synthesis output, decaying the window when it fills. */
export function recordProduction(state: GameState, produced: GoodTally): void {
  for (const id of GOOD_IDS) {
    const amount = produced[id];
    if (amount === undefined) continue;
    state.production.goods[id] += amount;
  }
  state.production.ticks += 1;

  if (state.production.ticks >= PRODUCTION_WINDOW_TICKS) {
    for (const id of GOOD_IDS) {
      state.production.goods[id] = Math.floor(state.production.goods[id] / 2);
    }
    state.production.ticks = Math.floor(state.production.ticks / 2);
  }
}

/**
 * Ticks until the shortfall is covered at current production.
 *
 * Returns null when any missing good has no production at all — the honest
 * answer there is "not on current course", not a very large number that implies
 * it is merely slow.
 */
function projectWait(state: GameState, missing: GoodTally): number | null {
  let worst = 0;

  for (const id of GOOD_IDS) {
    const shortfall = missing[id];
    if (shortfall === undefined || shortfall <= 0) continue;

    const producedInWindow = state.production.goods[id];
    if (producedInWindow <= 0 || state.production.ticks <= 0) return null;

    // ticks = shortfall / (produced per tick), kept in integers.
    const ticks = Math.ceil((shortfall * state.production.ticks) / producedInWindow);
    worst = Math.max(worst, ticks);
  }

  return worst;
}

function shortfall(state: GameState, cost: GoodTally): GoodTally {
  const missing: GoodTally = {};
  for (const id of GOOD_IDS) {
    const required = cost[id];
    if (required === undefined) continue;
    const gap = required - state.inventory.goods[id];
    if (gap > 0) missing[id] = gap;
  }
  return missing;
}

/** Total goods the whole queue has committed the player to. spec §9 */
export function committedCost(state: GameState): Record<GoodId, number> {
  const totals = {} as Record<GoodId, number>;
  for (const id of GOOD_IDS) totals[id] = 0;

  for (const item of state.buildQueue) {
    const cost = BUILDINGS[item.building].cost;
    for (const id of GOOD_IDS) {
      const amount = cost[id];
      if (amount !== undefined) totals[id] += amount;
    }
  }
  return totals;
}

/**
 * Queue one item per plot, spreading outward from the factory.
 * spec §9 "Order placement", plus the adjacency rule.
 *
 * Placement is breadth-first rather than row-major: each pass queues every
 * candidate that currently touches the city, then the next pass sees the city
 * those additions created. The queue therefore comes out ordered by distance
 * from the factory, so a drag-select builds from the factory outward instead of
 * starting at whichever plot happened to sort first.
 *
 * Row-major ordering survives WITHIN each wave, so placement stays deterministic
 * (spec §11 rule 3).
 *
 * Candidates that never connect — a rectangle drawn out in open ground — are
 * skipped, the same way §9 skips occupied plots. Queuing them instead would
 * deadlock the head-of-line block against a plot that can never be reached.
 */
export function queueBuild(
  state: GameState,
  plots: PlotCoord[],
  building: BuildingId,
): void {
  const remaining = [...plots]
    .filter((coord) => isBuildable(state, coord))
    .sort((a, b) => a.y - b.y || a.x - b.x);

  // Deduplicate: the same plot listed twice would otherwise be queued twice.
  const seen = new Set<string>();
  const candidates = remaining.filter((coord) => {
    const key = `${coord.x},${coord.y}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  let pending = candidates;

  while (pending.length > 0) {
    // Compute the whole wave before placing any of it, so one pass is a clean
    // distance ring rather than a cascade in scan order.
    const wave = pending.filter((coord) => isConnectedForPlanning(state, coord));
    if (wave.length === 0) break;

    for (const coord of wave) {
      state.city.grid[coord.y]![coord.x] = {
        coord: { x: coord.x, y: coord.y },
        buildingType: building,
        // Materials are not consumed yet: queued is free to cancel. spec §9
        state: 'queued',
        progressTicks: 0,
        totalTicks: buildTicksFor(coord),
        assignedMechs: 0,
        enabled: false,
      };

      state.buildQueue.push({
        id: state.nextQueueItemId,
        plot: { x: coord.x, y: coord.y },
        building,
      });
      state.nextQueueItemId += 1;
    }

    const placed = new Set(wave.map((c) => `${c.x},${c.y}`));
    pending = pending.filter((c) => !placed.has(`${c.x},${c.y}`));
  }
}

export function cancelQueued(state: GameState, index: number): void {
  const item = state.buildQueue[index];
  if (item === undefined) return;

  const plot = plotAt(state, item.plot);
  // Only clear the grid if it is still queued. A racing command could have
  // started it, and a started build is not cancellable. spec §9
  if (plot !== null && plot.state === 'queued') {
    state.city.grid[item.plot.y]![item.plot.x] = null;
  }
  state.buildQueue.splice(index, 1);
}

export function reorderQueue(state: GameState, from: number, to: number): void {
  if (from === to) return;
  if (from < 0 || from >= state.buildQueue.length) return;
  if (to < 0 || to >= state.buildQueue.length) return;

  const [item] = state.buildQueue.splice(from, 1);
  if (item === undefined) return;
  state.buildQueue.splice(to, 0, item);
}

/** spec §9: only a completed building can be demolished. */
export function demolish(state: GameState, coord: PlotCoord): void {
  const plot = plotAt(state, coord);
  if (plot === null || plot.state !== 'complete') return;

  plot.state = 'demolishing';
  plot.progressTicks = 0;
  // Same mech-time as construction. spec §9
  plot.totalTicks = buildTicksFor(coord);
  plot.assignedMechs = 0;
}

/**
 * Step 6 — start whatever the head of the queue can start. spec §8 step 6
 *
 * Two independent limits. Materials are the spec's head-of-line block. Mech
 * capacity is the other: every plot under construction draws a mech, so the pool
 * size caps how many can run at once. Distinguishing them matters — a starved
 * indicator naming goods while the real constraint is mechs points the player at
 * the wrong fix.
 */
export function advanceBuildQueue(state: GameState): void {
  state.starved = null;
  // Recount rather than trusting last tick's figure: plots that completed in
  // step 7 released their mechs after the count was taken.
  state.mechs.assigned = activePlots(state).length;

  for (;;) {
    const item = state.buildQueue[0];
    if (item === undefined) return;

    const plot = plotAt(state, item.plot);
    if (plot === null || plot.state !== 'queued') {
      // Stale entry — the plot was cancelled or demolished out from under it.
      state.buildQueue.shift();
      continue;
    }

    // The adjacency rule, enforced at the moment work would begin rather than
    // only when the order was placed. A plot can lose its connection in between
    // — the neighbour that linked it to the city may have been cancelled — and
    // mechs cannot build somewhere they cannot reach.
    if (!isReachable(state, item.plot)) {
      state.starved = { reason: 'disconnected', missing: {}, projectedWaitTicks: null };
      return;
    }

    const activeCount = state.mechs.assigned;
    if (activeCount >= state.mechs.total) {
      state.starved = { reason: 'mechs', missing: {}, projectedWaitTicks: null };
      return;
    }

    const cost = BUILDINGS[item.building].cost;
    if (!canAfford(state, cost)) {
      const missing = shortfall(state, cost);
      const info: StarvedInfo = {
        reason: 'materials',
        missing,
        projectedWaitTicks: projectWait(state, missing),
      };
      state.starved = info;
      return;
    }

    // Consume at start, not at order. spec §9 is explicit that this is what
    // lets the player queue the entire grid on turn one.
    spend(state, cost);
    plot.state = 'building';
    plot.progressTicks = 0;
    plot.assignedMechs = 1;
    state.mechs.assigned += 1;
    state.buildQueue.shift();
  }
}
